import React, { useEffect, useState, useLayoutEffect } from 'react';
import axios from 'axios';
import { useAppContext } from '../../../Store/Store';

interface DigitalContentPdfScreenProps {
  onNext: () => void;
  resetToScreen?: (screenKey: string) => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

// Define responsive container styles
const containerStyle = {
  margin: '0 auto',
  padding: 'clamp(8px, 2vw, 20px)',
  width: '100%',
  maxWidth: '1100px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column' as const,
  boxSizing: 'border-box' as const,
};

const pdfContainerStyle = {
  width: '100%',
  flex: 1,
  minHeight: '300px',
  maxHeight: '75dvh',
  border: '1px solid #ccc',
  borderRadius: '5px',
  marginBottom: '20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  position: 'relative' as const,
};

const pdfViewerStyle = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  border: 'none',
};

const placeholderImageStyle = {
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain' as const,
};

const buttonContainerStyle = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: '10px',
  justifyContent: 'space-between',
  marginTop: '15px',
  width: '100%',
};

const buttonStyle = {
  minHeight: '44px',
  height: '50px',
  width: '48%',
  borderRadius: '7px',
  padding: '0 16px',
  fontSize: 'clamp(0.8rem, 2vw, 1rem)',
  fontWeight: 'bold' as const,
  border: 'none',
  cursor: 'pointer',
};

const loadingStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '50dvh',
  width: '100%',
  fontSize: '1.2rem',
};

// Add a white screen style for redirects
const redirectingStyle = {
  width: '100vw',
  height: '100dvh',
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'fixed' as const,
  top: 0,
  left: 0,
  zIndex: 9999,
};

const fallbackMessageStyle = {
  textAlign: 'center' as const,
  padding: '20px',
  backgroundColor: '#f9f9f9',
  border: '1px solid #ddd',
  borderRadius: '5px',
};

const linkStyle = {
  color: '#07296A',
  fontWeight: 'bold' as const,
  textDecoration: 'underline',
};

export default function DigitalContentPdfScreen({
  onNext,
  resetToScreen,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
}: DigitalContentPdfScreenProps) {
  const {
    backendBaseUrl,
    resolveMediaUrl,
    product,
    event,
    setGlobalNotification,
    style,
  } = useAppContext();
  const [loading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewerType, setViewerType] = useState<
    'iframe' | 'object' | 'embed' | 'fallback' | 'image'
  >('iframe');
  const [shouldRedirect] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [pdfOrientation] = useState<'portrait' | 'landscape'>('portrait');

  // Check for redirect conditions only on initial mount
  useLayoutEffect(() => {
    const checkForRedirects = async () => {
      try {
        // Priority chain implementation:

        // 1. Product redirect_product - skip digital content and proceed to next screen
        if (product?.redirect_product) {
          onNext();
          return true;
        }

        // 2. Product redirect_digital_content
        else if (product?.redirect_digital_content) {
          setIsRedirecting(true);

          // Add small delay to ensure state is set before redirect
          await new Promise(resolve => setTimeout(resolve, 10));

          window.open(product.redirect_digital_content, '_blank');
          onNext();
          return true;
        }

        // 3. Event redirect_digital_content
        else if (event?.redirect_digital_content) {
          setIsRedirecting(true);

          // Add small delay to ensure state is set before redirect
          await new Promise(resolve => setTimeout(resolve, 10));

          window.open(event.redirect_digital_content, '_blank');
          onNext();
          return true;
        }

        return false;
      } catch {
        return false;
      }
    };

    checkForRedirects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally runs once on mount

  // Separate effect for content setup when product/event changes
  useEffect(() => {
    // Don't setup content if we're redirecting
    if (isRedirecting) return;

    setupContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, event, isRedirecting]);

  const setupContent = () => {
    try {
      // 3. Product digital_content_placeholder_img
      if (product?.digital_content_placeholder_img) {
        setPdfUrl(null);
        setViewerType('image');
      }

      // 4. Product digital_content_pdf
      else if (product?.digital_content_pdf) {
        setPdfUrl(product.digital_content_pdf);
        setupPdfViewer();
      }

      // 6. Event digital_content_placeholder_img
      else if (event?.digital_content_placeholder_img) {
        setPdfUrl(null);
        setViewerType('image');
      }

      // 7. Event digital_content_pdf
      else if (event?.digital_content_pdf) {
        setPdfUrl(event.digital_content_pdf);
        setupPdfViewer();
      } else {
        // No content available
        setError('No content available');
      }
    } catch {
      setError('Failed to load content');
      setGlobalNotification(['Failed to load content', 'error']);
    }
  };

  const setupPdfViewer = () => {
    const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
    const isChrome = navigator.userAgent.toLowerCase().indexOf('chrome') > -1;
    const isSafari =
      navigator.userAgent.toLowerCase().indexOf('safari') > -1 && !isChrome;

    if (isFirefox) {
      setViewerType('object');
    } else if (isChrome) {
      setViewerType('iframe');
    } else if (isSafari) {
      setViewerType('embed');
    } else {
      setViewerType('iframe');
    }
  };

  // Show white screen during redirects
  if (isRedirecting) {
    return (
      <div style={redirectingStyle}>
        {/* Optional: You can show nothing (white screen) or a minimal loading indicator */}
      </div>
    );
  }

  if (shouldRedirect) {
    return null;
  }

  const handleDownload = async (url: string) => {
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', `digital-content-${product.code}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      setGlobalNotification(['Download fallito', 'error']);
    }
  };

  const handleBackToFeedback = () => {
    if (resetToScreen) {
      resetToScreen('feedback');
    } else {
      // Fallback to history.back() if resetToScreen is not available
      window.history.back();
    }
  };

  const handleViewerError = () => {
    setViewerType(prev => {
      if (prev === 'iframe') return 'object';
      if (prev === 'object') return 'embed';
      return 'fallback';
    });
  };

  const renderContent = () => {
    // Priority chain implementation:
    // 1. Product digital content placeholder image
    if (viewerType === 'image') {
      return (
        <div style={pdfContainerStyle}>
          <img
            src={resolveMediaUrl(product.digital_content_placeholder_img)}
            alt="Digital Content"
            style={placeholderImageStyle}
          />
        </div>
      );
    }

    // 2. PDF Viewer
    if (pdfUrl) {
      return renderPdfViewer(pdfUrl);
    }

    return null;
  };

  const renderPdfViewer = (url: string) => {
    const viewerContainerStyle = {
      ...pdfContainerStyle,
      padding: pdfOrientation === 'landscape' ? '2vh 0' : '0 2vh',
    };

    switch (viewerType) {
      case 'iframe':
        return (
          <div style={viewerContainerStyle}>
            <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`}
              style={pdfViewerStyle}
              title="PDF Viewer"
            />
          </div>
        );

      case 'object':
        return (
          <div style={viewerContainerStyle}>
            <object data={url} type="application/pdf" style={pdfViewerStyle}>
              <p style={fallbackMessageStyle}>
                Unable to display PDF.{' '}
                <a
                  href={url}
                  style={linkStyle}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Click here to view
                </a>
              </p>
            </object>
          </div>
        );

      case 'embed':
        return (
          <div style={viewerContainerStyle}>
            <embed src={url} type="application/pdf" style={pdfViewerStyle} />
          </div>
        );

      case 'fallback':
      default:
        return (
          <div
            style={{
              ...pdfContainerStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              padding: '20px',
            }}
          >
            <p style={{ fontSize: '1.2rem', marginBottom: '20px' }}>
              Your browser cannot display the PDF directly.
            </p>
            <button
              onClick={() => handleDownload(url)}
              style={{
                padding: '15px 30px',
                backgroundColor: '#07296A',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              Open PDF in New Tab
            </button>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>Loading digital content...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>{error}</div>
        <div style={buttonContainerStyle}>
          <button
            onClick={handleBackToFeedback}
            style={{
              ...buttonStyle,
              width: '100%',
              backgroundColor: '#07296A',
              color: 'white',
            }}
          >
            Back to Feedback
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {renderContent()}
      <div style={buttonContainerStyle}>
        <button
          onClick={() =>
            handleDownload(
              pdfUrl ||
                product?.digital_content_pdf_url ||
                event?.digital_content_pdf_url ||
                ''
            )
          }
          style={{
            ...buttonStyle,
            backgroundColor: '#07296A',
            color: 'white',
          }}
        >
          {viewerType === 'fallback' ? 'Open PDF' : 'Download'}
        </button>
        <button
          onClick={handleBackToFeedback}
          style={{
            ...buttonStyle,
            backgroundColor: '#f0f0f0',
            color: '#333',
            border: '1px solid #ccc',
          }}
        >
          Back to Feedback
        </button>
      </div>
      {showBackButton && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px',
          }}
        >
          <button
            type="button"
            onClick={() => onBack?.()}
            style={{
              fontSize: '18px',
              textDecoration: 'underline',
              color: style?.back_button_text_color || '#07296A',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: style?.back_button_indication || 'Back',
              }}
            />
          </button>
        </div>
      )}
      {showSkipNextScreenButton && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px',
          }}
        >
          <button
            type="button"
            onClick={onNext}
            style={{
              fontSize: '18px',
              textDecoration: 'underline',
              color: style?.skip_next_screen_button_text_color || '#07296A',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: style?.skip_next_screen_button_indication || 'Skip',
              }}
            />
          </button>
        </div>
      )}
    </div>
  );
}
